import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { SelectField, InputField, TextAreaField } from "@/components/ui/FormFields";
import { Modal } from "@/components/ui/Modal";
import { ApiError } from "@/api/client";
import {
  decideDharoharRecord,
  getDharoharModerationQueue,
  getDharoharRecordForReview,
  getDharoharRecordsForReview,
  processDharoharModeration,
  type DharoharReviewContentType,
  type DharoharReviewRecordSummary,
  type DharoharReviewSource,
  type DharoharReviewStatus,
  type DharoharSubmission,
} from "@/api/admin";

type View = "records" | "sources";
type ModerationFilter = "" | "pending_review" | "approved" | "rejected";

const CONTENT_TYPES: { value: DharoharReviewContentType; label: string }[] = [
  { value: "personality", label: "Personalities" },
  { value: "art_form", label: "Art forms" },
  { value: "handicraft", label: "Handicrafts" },
  { value: "significance", label: "Date significance" },
];

const REVIEW_STATUSES: { value: DharoharReviewStatus; label: string }[] = [
  { value: "pending_review", label: "Waiting for review" },
  { value: "approved", label: "Published" },
  { value: "rejected", label: "Rejected" },
  { value: "unsourced", label: "No current source" },
];

export default function DharoharModerationLab() {
  const [view, setView] = useState<View>("records");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dharohar Review"
        subtitle="Read a record once, with every source it cites, then approve or reject the whole record."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Dharohar Review" },
        ]}
        actions={
          <>
            <Button
              size="sm"
              variant={view === "records" ? "primary" : "secondary"}
              onClick={() => setView("records")}
            >
              By record
            </Button>
            <Button
              size="sm"
              variant={view === "sources" ? "primary" : "secondary"}
              onClick={() => setView("sources")}
            >
              By source
            </Button>
          </>
        }
      />
      {view === "records" ? <RecordQueue /> : <SourceQueue />}
    </div>
  );
}

function ReviewBadge({ status }: { status: string }) {
  if (status === "approved") return <Badge variant="green">published</Badge>;
  if (status === "rejected") return <Badge variant="red">rejected</Badge>;
  if (status === "pending_review") return <Badge variant="yellow">waiting</Badge>;
  return <Badge variant="gray">{status.replace(/_/g, " ")}</Badge>;
}

function SourceStatusBadge({ status }: { status: string }) {
  const variant =
    status === "approved"
      ? "green"
      : status === "rejected" || status === "error"
        ? "red"
        : status === "pending_review"
          ? "yellow"
          : "gray";
  return <Badge variant={variant}>{status.replace(/_/g, " ")}</Badge>;
}

// ─── By record ─────────────────────────────────────────────────────

function RecordQueue() {
  const qc = useQueryClient();
  const [contentType, setContentType] = useState<DharoharReviewContentType>("personality");
  const [status, setStatus] = useState<DharoharReviewStatus>("pending_review");
  const [stateCode, setStateCode] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  // The API accepts only a full two-letter code, so a half-typed one filters nothing.
  const typedState = stateCode.trim().toUpperCase();
  const stateFilter = typedState.length === 2 ? typedState : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-dharohar-records", contentType, status, stateFilter, page],
    queryFn: () =>
      getDharoharRecordsForReview({
        contentType,
        status,
        stateCode: stateFilter,
        page,
        limit: 20,
      }),
  });
  const queue = data?.data;

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-dharohar-records"] });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3 max-w-3xl">
        <SelectField
          label="Content"
          value={contentType}
          onChange={(e) => {
            setContentType(e.target.value as DharoharReviewContentType);
            setPage(1);
          }}
          options={CONTENT_TYPES}
        />
        <SelectField
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as DharoharReviewStatus);
            setPage(1);
          }}
          options={REVIEW_STATUSES}
        />
        <InputField
          label="State code"
          placeholder="GJ"
          maxLength={2}
          value={stateCode}
          onChange={(e) => {
            setStateCode(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {queue && (
        <p className="text-sm text-gray-600">
          {queue.counts.pendingReview} waiting · {queue.counts.approved} published ·{" "}
          {queue.counts.rejected} rejected · {queue.counts.unsourced} with no current source
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-700">{(error as Error).message}</p>
      ) : !queue || queue.records.length === 0 ? (
        <p className="text-sm text-gray-500">No records.</p>
      ) : (
        <ul className="space-y-2">
          {queue.records.map((record) => (
            <RecordRow key={record.id} record={record} onReview={() => setOpenId(record.id)} />
          ))}
        </ul>
      )}

      {queue && queue.meta.totalPages > 1 && (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-xs text-gray-500">
            Page {queue.meta.page} of {queue.meta.totalPages}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={page >= queue.meta.totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {openId && (
        <RecordReviewModal
          contentType={contentType}
          id={openId}
          onClose={() => setOpenId(null)}
          onDecided={refresh}
        />
      )}
    </div>
  );
}

function RecordRow({
  record,
  onReview,
}: {
  record: DharoharReviewRecordSummary;
  onReview: () => void;
}) {
  return (
    <li className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">{record.name}</span>
          {record.nameLocal && (
            <span className="text-sm text-gray-500">{record.nameLocal}</span>
          )}
          <ReviewBadge status={record.reviewStatus} />
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {record.stateCode ?? "no state"} · {record.sourceCount}{" "}
          {record.sourceCount === 1 ? "source" : "sources"}
          {record.contentKey && ` · ${record.contentKey}`}
        </div>
      </div>
      <Button size="sm" variant="secondary" onClick={onReview}>
        Review
      </Button>
    </li>
  );
}

function RecordReviewModal({
  contentType,
  id,
  onClose,
  onDecided,
}: {
  contentType: DharoharReviewContentType;
  id: string;
  onClose: () => void;
  onDecided: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-dharohar-record", contentType, id],
    queryFn: () => getDharoharRecordForReview(contentType, id),
  });
  const review = data?.data;

  async function decide(action: "approve" | "reject") {
    if (!review) return;
    setBusy(action);
    setFailure(null);
    try {
      await decideDharoharRecord(contentType, id, {
        action,
        contentHash: review.contentHash,
        reason: action === "reject" ? reason.trim() : undefined,
      });
      onDecided();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === "RECORD_CHANGED") {
        // The content changed after this read. Load the new content to review.
        await qc.invalidateQueries({ queryKey: ["admin-dharohar-record", contentType, id] });
      }
      setFailure(err instanceof Error ? err.message : "The decision failed.");
    } finally {
      setBusy(null);
    }
  }

  const title = review
    ? String(review.record.name ?? review.record.title ?? "Record")
    : "Record";
  const canApprove = review?.allowedActions.includes("approve") ?? false;
  const canReject = review?.allowedActions.includes("reject") ?? false;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {canReject && (
            <Button
              variant="danger"
              onClick={() => decide("reject")}
              isLoading={busy === "reject"}
              disabled={!reason.trim() || busy !== null}
            >
              Reject record
            </Button>
          )}
          {canApprove && review && (
            <Button
              onClick={() => decide("approve")}
              isLoading={busy === "approve"}
              disabled={busy !== null}
            >
              Approve record and {review.sources.length}{" "}
              {review.sources.length === 1 ? "source" : "sources"}
            </Button>
          )}
        </>
      }
    >
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error || !review ? (
        <p className="text-sm text-red-700">
          {error ? (error as Error).message : "Record not found."}
        </p>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <ReviewBadge status={review.reviewStatus} />
            {!review.isActive && <Badge variant="gray">retired</Badge>}
            {review.contentKey && (
              <span className="text-xs text-gray-500">{review.contentKey}</span>
            )}
          </div>

          <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2 text-sm">
            {Object.entries(review.record).map(([field, value]) => (
              <FieldRow key={field} field={field} value={value} />
            ))}
          </dl>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Sources</h3>
            {review.sources.length === 0 ? (
              <p className="text-sm text-gray-500">No current source.</p>
            ) : (
              <ul className="space-y-2">
                {review.sources.map((source) => (
                  <SourceItem key={source.id} source={source} />
                ))}
              </ul>
            )}
          </div>

          {canReject && (
            <TextAreaField
              label="Reason to reject"
              hint="Required to reject. The corpus maintainer reads it."
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
          {failure && <p className="text-sm text-red-700">{failure}</p>}
        </div>
      )}
    </Modal>
  );
}

function FieldRow({ field, value }: { field: string; value: unknown }) {
  const empty =
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);
  const text = empty
    ? "—"
    : Array.isArray(value)
      ? value.join(", ")
      : typeof value === "object"
        ? JSON.stringify(value)
        : String(value);
  return (
    <>
      <dt className="text-gray-500">{field}</dt>
      <dd className={`whitespace-pre-wrap break-words ${empty ? "text-gray-400" : "text-gray-900"}`}>
        {text}
      </dd>
    </>
  );
}

function SourceItem({ source }: { source: DharoharReviewSource }) {
  // A manifest URL passes a URL check, which does not rule out a script URL.
  const linkable = /^https?:\/\//i.test(source.sourceUrl);
  return (
    <li className="border border-gray-200 rounded-lg px-3 py-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant={
            source.tier === "primary"
              ? "purple"
              : source.tier === "authoritative"
                ? "blue"
                : "gray"
          }
        >
          {source.tier ?? "no tier"}
        </Badge>
        <span className="text-sm font-medium text-gray-900">{source.sourceName}</span>
        <SourceStatusBadge status={source.status} />
      </div>
      {linkable ? (
        <a
          href={source.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-blue-700 break-all"
        >
          {source.sourceUrl}
        </a>
      ) : (
        <p className="text-xs text-gray-500 break-all">{source.sourceUrl}</p>
      )}
      <p className="text-xs text-gray-500 mt-1">
        Supports: {source.claimKeys.length ? source.claimKeys.join(", ") : "not stated"}
      </p>
      {source.errorMessage && (
        <p className="text-xs text-red-700 mt-1">Reason: {source.errorMessage}</p>
      )}
    </li>
  );
}

// ─── By source ─────────────────────────────────────────────────────

function SourceQueue() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ModerationFilter>("pending_review");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-dharohar-moderation", status],
    queryFn: () =>
      getDharoharModerationQueue({ status: status || undefined }),
  });

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["admin-dharohar-moderation"] });

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        One row for each source. Use it for a record whose sources are in different
        states. Decide whole records in the record view.
      </p>

      <div className="max-w-xs">
        <SelectField
          label="Filter"
          value={status}
          onChange={(e) => setStatus(e.target.value as ModerationFilter)}
          options={[
            { value: "", label: "All" },
            { value: "pending_review", label: "Pending review" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
          ]}
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data || data.data.sources.length === 0 ? (
        <p className="text-sm text-gray-500">No submissions.</p>
      ) : (
        <ul className="space-y-2">
          {data.data.sources.map((s) => (
            <SubmissionRow key={s.id} submission={s} onChange={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SubmissionRow({
  submission,
  onChange,
}: {
  submission: DharoharSubmission;
  onChange: () => void;
}) {
  const [modal, setModal] = useState<"approve" | "reject" | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  // Entity fields live under contentDetails; fall back to the source name.
  const details = submission.contentDetails;
  const title =
    (details?.name as string) ||
    (details?.title as string) ||
    submission.sourceName ||
    "(untitled source)";
  const stateCode = details?.stateCode as string | undefined;

  async function handleProcess(action: "approve" | "reject") {
    setBusy(true);
    try {
      // moderationActionSchema only accepts { action, reason? }.
      await processDharoharModeration(submission.id, {
        action,
        reason:
          (action === "reject" ? reason : notes).trim() || undefined,
      });
      onChange();
      close();
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setModal(null);
    setReason("");
    setNotes("");
  }

  const canAct = submission.status === "pending_review";
  return (
    <li className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">{title}</span>
          <Badge variant="gray">{submission.contentType.replace("_", " ")}</Badge>
          <SourceStatusBadge status={submission.status} />
        </div>
        {submission.sourceUrl && (
          <p className="text-xs text-gray-500 mt-1 truncate">
            {submission.sourceName ? `${submission.sourceName} · ` : ""}
            {submission.sourceUrl}
          </p>
        )}
        <div className="text-xs text-gray-500 mt-1">
          {stateCode && `${stateCode} · `}
          fetched {new Date(submission.createdAt).toLocaleString()}
        </div>
        {submission.errorMessage && (
          <p className="text-xs text-red-700 mt-1">
            Error: {submission.errorMessage}
          </p>
        )}
      </div>
      {canAct && (
        <div className="flex gap-1 shrink-0">
          <Button size="sm" onClick={() => setModal("approve")}>
            Approve
          </Button>
          <Button size="sm" variant="danger" onClick={() => setModal("reject")}>
            Reject
          </Button>
        </div>
      )}

      <Modal
        isOpen={modal === "approve"}
        onClose={close}
        title={`Approve "${title}"`}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={() => handleProcess("approve")} isLoading={busy}>
              Approve
            </Button>
          </>
        }
      >
        <InputField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Modal>

      <Modal
        isOpen={modal === "reject"}
        onClose={close}
        title={`Reject "${title}"`}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => handleProcess("reject")}
              isLoading={busy}
              disabled={!reason.trim()}
            >
              Reject
            </Button>
          </>
        }
      >
        <InputField
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Modal>
    </li>
  );
}
